/**
 * VeriClean API Service - ISOLATED from VeriTrade API
 * Uses separate cookie (vericlean_auth_token) and endpoints
 * iOS-safe storage implementation
 */

import { getLocalItem, setLocalItem, removeLocalItem } from '@/utils/safeStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// VeriClean-specific localStorage key (separate from VeriTrade)
const VERICLEAN_TOKEN_KEY = 'vericlean_auth_token';

/**
 * Get stored VeriClean token (iOS safe)
 */
export const getVeriCleanToken = () => {
  return getLocalItem(VERICLEAN_TOKEN_KEY);
};

/**
 * Set VeriClean token (iOS safe)
 */
export const setVeriCleanToken = (token) => {
  if (token) {
    setLocalItem(VERICLEAN_TOKEN_KEY, token);
  } else {
    removeLocalItem(VERICLEAN_TOKEN_KEY);
  }
};

/**
 * Make a VeriClean API request
 */
const vericleanFetch = async (endpoint, options = {}) => {
  const token = getVeriCleanToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Include cookies
  });
  
  return response;
};

/**
 * VeriClean Auth API
 */
export const vericleanAuth = {
  /**
   * Sign up for VeriClean
   */
  signup: async (data) => {
    const response = await vericleanFetch('/api/vericlean/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },
  
  /**
   * Login to VeriClean
   * IMPORTANT: Clears ALL previous session data before login
   */
  login: async (email, password) => {
    // CRITICAL: Clear ALL previous session data BEFORE login
    setVeriCleanToken(null);
    // Also clear VeriTrade tokens to prevent cross-contamination
    removeLocalItem('auth_token');
    removeLocalItem('token');
    removeLocalItem('user');
    sessionStorage.clear();
    console.log('[VeriClean] Cleared all previous session data');
    
    const response = await vericleanFetch('/api/vericlean/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    
    if (data.ok && data.token) {
      setVeriCleanToken(data.token);
    }
    
    return data;
  },
  
  /**
   * Logout from VeriClean
   * IMPORTANT: Clears ALL session data
   */
  logout: async () => {
    try {
      await vericleanFetch('/api/vericlean/logout', {
        method: 'POST',
      });
    } finally {
      // Clear VeriClean token
      setVeriCleanToken(null);
      // Also clear any VeriTrade tokens to ensure complete session isolation
      removeLocalItem('auth_token');
      removeLocalItem('token');
      removeLocalItem('user');
      sessionStorage.clear();
      console.log('[VeriClean] Logout complete - all session data cleared');
    }
  },
  
  /**
   * Get current VeriClean user
   */
  me: async () => {
    const response = await vericleanFetch('/api/vericlean/me');
    if (!response.ok) {
      throw new Error('Not authenticated');
    }
    return response.json();
  },
};

/**
 * VeriClean Jobs API - ISOLATED from VeriTrade jobs
 */
export const vericleanJobs = {
  /**
   * P0 unified flow — create/reuse customer account AND post the cleaning job
   * in a single atomic call. Send a stable idempotency_key from the funnel so
   * double-clicks / retries don't create duplicates.
   *
   * Handles 502 responses (account created but job failed) by surfacing them
   * to the caller for retry — the response body carries the same token so the
   * client can hold session and try `create()` separately if needed.
   */
  submitCustomerJob: async (payload) => {
    const response = await vericleanFetch('/api/vericlean/customer/submit-job', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    // 502 = account created, job failed — body still carries token + recovery info
    let data;
    try {
      data = await response.json();
    } catch {
      data = { ok: false, error: 'Network error — please retry.' };
    }
    // If the response carries a session token, store it so the user is logged in
    const tokenFromBody = data?.token || data?.detail?.token;
    if (tokenFromBody) setVeriCleanToken(tokenFromBody);
    return { httpStatus: response.status, ...data, ...(data?.detail || {}) };
  },

  /**
   * Create a cleaning job (customer only)
   */
  create: async (jobData) => {
    const response = await vericleanFetch('/api/vericlean/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
    return response.json();
  },
  
  /**
   * List jobs (role-aware: customers see own, cleaners see open)
   */
  list: async (page = 1, limit = 25) => {
    const response = await vericleanFetch(`/api/vericlean/jobs?page=${page}&limit=${limit}`);
    return response.json();
  },
  
  /**
   * Get a single job by ID
   */
  get: async (jobId) => {
    const response = await vericleanFetch(`/api/vericlean/jobs/${jobId}`);
    return response.json();
  },
  
  /**
   * Update a job (owner only)
   */
  update: async (jobId, updates) => {
    const response = await vericleanFetch(`/api/vericlean/jobs/${jobId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.json();
  },
};

/**
 * VeriClean Profiles API - ISOLATED from VeriTrade profiles
 */
export const vericleanProfiles = {
  /**
   * Get current cleaner's profile
   */
  get: async () => {
    const response = await vericleanFetch('/api/vericlean/profile');
    return response.json();
  },
  
  /**
   * Update current cleaner's profile
   */
  update: async (profileData) => {
    const response = await vericleanFetch('/api/vericlean/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return response.json();
  },
  
  /**
   * List/search cleaners (public)
   */
  listCleaners: async (page = 1, limit = 25, search = '', location = '') => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    if (location) params.append('location', location);
    const response = await vericleanFetch(`/api/vericlean/cleaners?${params.toString()}`);
    return response.json();
  },
  
  /**
   * Get cleaner's public profile
   */
  getCleaner: async (cleanerId) => {
    const response = await vericleanFetch(`/api/vericlean/cleaners/${cleanerId}`);
    return response.json();
  },
};

/**
 * VeriClean Messaging API - ISOLATED from VeriTrade messaging
 */
export const vericleanMessaging = {
  /**
   * List conversations
   */
  listConversations: async () => {
    const response = await vericleanFetch('/api/vericlean/conversations');
    return response.json();
  },
  
  /**
   * Create or get conversation. Returns { ok, status, data } so callers can branch on
   * 403 (free_limit_reached) without losing the parsed body.
   *
   * Implemented with XMLHttpRequest rather than fetch because emergent's preview
   * environment injects a session-replay wrapper around `window.fetch` (rrweb)
   * that races with response.clone() and can swallow non-2xx responses. XHR is
   * unaffected and keeps the upgrade-modal flow reliable.
   */
  createConversation: async (cleanerAccountId, jobId = null) => {
    const url = `${BACKEND_URL}/api/vericlean/conversations`;
    const token = getVeriCleanToken();
    const body = JSON.stringify({ cleaner_account_id: cleanerAccountId, job_id: jobId });
    return new Promise((resolve) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let data = {};
          try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { data = {}; }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        };
        xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'network_error' } });
        xhr.send(body);
      } catch (err) {
        resolve({ ok: false, status: 0, data: { detail: err?.message || 'request_failed' } });
      }
    });
  },
  
  /**
   * Get conversation details
   */
  getConversation: async (conversationId) => {
    const response = await vericleanFetch(`/api/vericlean/conversations/${conversationId}`);
    return response.json();
  },
  
  /**
   * List messages in conversation
   */
  listMessages: async (conversationId, page = 1, limit = 50) => {
    const response = await vericleanFetch(`/api/vericlean/conversations/${conversationId}/messages?page=${page}&limit=${limit}`);
    return response.json();
  },
  
  /**
   * Send message
   */
  sendMessage: async (conversationId, body) => {
    const response = await vericleanFetch(`/api/vericlean/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
    return response.json();
  },
  
  /**
   * Get contact access status
   */
  getContactStatus: async (conversationId) => {
    const response = await vericleanFetch(`/api/vericlean/contact-access/status?conversation_id=${conversationId}`);
    return response.json();
  },
  
  /**
   * Grant contact access (customer only)
   */
  grantContactAccess: async (conversationId) => {
    const response = await vericleanFetch('/api/vericlean/contact-access/grant', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId }),
    });
    return response.json();
  },
  
  /**
   * Revoke contact access (customer only)
   */
  revokeContactAccess: async (conversationId) => {
    const response = await vericleanFetch('/api/vericlean/contact-access/revoke', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId }),
    });
    return response.json();
  },

  /**
   * Get the cleaner's free-tier contact usage for the current calendar month.
   * Returns { ok, tier, unlimited, used, limit, remaining, limit_reached }.
   */
  getContactUsage: async () => {
    const response = await vericleanFetch('/api/vericlean/contact-usage');
    return response.json();
  },
};

/**
 * Stripe billing helpers (VeriClean)
 */
export const vericleanBilling = {
  /**
   * Sync a Stripe checkout session immediately after redirect — fallback when the
   * webhook is delayed/missed. Retries up to `maxAttempts` times if the session
   * isn't yet complete on Stripe's side.
   */
  syncCheckoutSession: async (sessionId, { maxAttempts = 3, delayMs = 1500 } = {}) => {
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await vericleanFetch('/api/stripe/sync-checkout-session', {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId }),
        });
        const text = await response.text().catch(() => '');
        let data = {};
        try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
        if (response.ok && data.success) {
          return data;
        }
        lastErr = data;
      } catch (err) {
        lastErr = { error: err?.message || 'network_error' };
      }
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    return { success: false, ...(lastErr || {}) };
  },
};

export default {
  auth: vericleanAuth,
  jobs: vericleanJobs,
  profiles: vericleanProfiles,
  messaging: vericleanMessaging,
  billing: vericleanBilling,
  fetch: vericleanFetch,
  getToken: getVeriCleanToken,
  setToken: setVeriCleanToken,
};
